import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Header } from '@/components/layout'
import { Button } from '@/components/common'
import { api } from '@/services/api'
import '@/assets/styles/pages/pricing.css'

const subscriptionPlans = [
  {
    id: 'starter',
    name: 'Starter',
    monthlyPrice: 19.99,
    yearlyPrice: 199,
    monthlyCredits: 16000,
    yearlyCredits: 192000,
    features: [
      '16,000 credits per month',
      'All AI platforms supported',
      'Content Studio access',
      'GEO Score analysis',
      'Email support',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    monthlyPrice: 199.99,
    yearlyPrice: 1990,
    monthlyCredits: 200000,
    yearlyCredits: 2400000,
    popular: true,
    features: [
      '200,000 credits per month',
      'All AI platforms supported',
      'Content Studio access',
      'GEO Score analysis',
      'Priority support',
      'Advanced analytics',
    ],
  },
]

const creditPacks = [
  {
    id: 'pack-small',
    credits: 6000,
    price: 9.9,
  },
  {
    id: 'pack-large',
    credits: 30000,
    price: 49.9,
  },
]

export const PricingPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly')
  const [loadingPlanId, setLoadingPlanId] = useState<string | null>(null)
  const [paymentStatus, setPaymentStatus] = useState<'success' | 'cancelled' | null>(null)
  const [creditsAdded, setCreditsAdded] = useState<string | null>(null)

  // Handle Stripe redirect callback
  useEffect(() => {
    const sessionId = searchParams.get('session_id')
    const statusParam = searchParams.get('status')

    if (statusParam === 'cancelled') {
      setPaymentStatus('cancelled')
      setSearchParams({}, { replace: true })
      return
    }

    if (sessionId && statusParam === 'success') {
      let attempts = 0
      const maxAttempts = 30

      const poll = async () => {
        try {
          const resp = await api.payment.checkSession(sessionId)
          if (resp.status === 'paid') {
            setPaymentStatus('success')
            setCreditsAdded(resp.credits_added)
            setSearchParams({}, { replace: true })
            return
          }
          if (resp.status === 'failed' || resp.status === 'expired') {
            setPaymentStatus('cancelled')
            setSearchParams({}, { replace: true })
            return
          }
        } catch {
          // ignore poll errors
        }

        attempts++
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000)
        } else {
          setPaymentStatus('success')
          setSearchParams({}, { replace: true })
        }
      }

      poll()
    }
  }, [])

  const handlePurchase = async (planId: string) => {
    setLoadingPlanId(planId)
    try {
      const resp = await api.payment.createSession(planId)
      if (resp.success && resp.checkout_url) {
        window.location.href = resp.checkout_url
      }
    } catch (error) {
      console.error('Failed to create checkout session:', error)
      alert('Failed to start checkout. Please try again.')
    } finally {
      setLoadingPlanId(null)
    }
  }

  return (
    <>
      <Header
        title="Pricing"
        subtitle="Choose a plan or buy credits as you need them"
      />

      <div className="main-body">
        <div className="pricing-container">
          {/* Payment Status Banners */}
          {paymentStatus === 'success' && (
            <div className="pricing-banner pricing-banner-success">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '20px', height: '20px', flexShrink: 0 }}>
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <div>
                <strong>Payment successful!</strong>
                {creditsAdded && <span> {Number(creditsAdded).toLocaleString()} credits have been added to your account.</span>}
              </div>
              <button className="pricing-banner-close" onClick={() => setPaymentStatus(null)}>&times;</button>
            </div>
          )}

          {paymentStatus === 'cancelled' && (
            <div className="pricing-banner pricing-banner-cancelled">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '20px', height: '20px', flexShrink: 0 }}>
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
              <div>Payment was cancelled. No charges were made.</div>
              <button className="pricing-banner-close" onClick={() => setPaymentStatus(null)}>&times;</button>
            </div>
          )}

          {/* Subscription Plans */}
          <div className="pricing-section">
            <h3 className="pricing-section-title">Monthly Plans</h3>
            <p className="pricing-section-desc">Subscribe for a monthly credit allocation</p>

            {/* Billing Cycle Toggle */}
            <div className="billing-toggle">
              <button
                className={`billing-toggle-btn ${billingCycle === 'monthly' ? 'active' : ''}`}
                onClick={() => setBillingCycle('monthly')}
              >
                Monthly
              </button>
              <button
                className={`billing-toggle-btn ${billingCycle === 'yearly' ? 'active' : ''}`}
                onClick={() => setBillingCycle('yearly')}
              >
                Yearly
                <span className="billing-save-badge">Save 17%</span>
              </button>
            </div>

            <div className="pricing-plans">
              {subscriptionPlans.map((plan) => {
                const isYearly = billingCycle === 'yearly'
                const credits = isYearly ? plan.yearlyCredits : plan.monthlyCredits
                const planId = `${plan.id}-${billingCycle}`
                const monthlyEquiv = isYearly ? (plan.yearlyPrice / 12).toFixed(2) : null
                const primaryPrice = isYearly ? monthlyEquiv : plan.monthlyPrice
                const primaryPeriod = '/month'
                const yearlyEquiv = isYearly ? plan.yearlyPrice : null

                return (
                  <div
                    key={plan.id}
                    className={`pricing-card ${plan.popular ? 'pricing-card-popular' : ''}`}
                  >
                    {plan.popular && (
                      <div className="pricing-popular-badge">Most Popular</div>
                    )}
                    <div className="pricing-card-header">
                      <h4 className="pricing-plan-name">{plan.name}</h4>
                      <div className="pricing-price">
                        <span className="pricing-currency">$</span>
                        <span className="pricing-amount">{primaryPrice}</span>
                        <span className="pricing-period">{primaryPeriod}</span>
                      </div>
                      {yearlyEquiv != null && (
                        <div className="pricing-monthly-equiv">
                          ${yearlyEquiv}/year
                        </div>
                      )}
                      <div className="pricing-credits-highlight">
                        {credits.toLocaleString()} credits{isYearly ? ' / year' : ''}
                      </div>
                    </div>

                    <ul className="pricing-features">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="pricing-feature">
                          <svg className="pricing-feature-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                          {feature}
                        </li>
                      ))}
                    </ul>

                    <Button
                      className="pricing-cta-btn"
                      onClick={() => handlePurchase(planId)}
                      disabled={loadingPlanId !== null}
                    >
                      {loadingPlanId === planId ? 'Redirecting…' : 'Get Started'}
                    </Button>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Pay As You Go */}
          <div className="pricing-section">
            <h3 className="pricing-section-title">Pay As You Go Credits</h3>
            <p className="pricing-section-desc">One-time purchase, credits never expire. Use for all AI features.</p>

            <div className="pricing-packs">
              {creditPacks.map((pack) => (
                <div key={pack.id} className="pricing-pack-card">
                  <div className="pricing-pack-top">
                    <div className="pricing-price">
                      <span className="pricing-currency">$</span>
                      <span className="pricing-amount">{pack.price}</span>
                    </div>
                    <span className="pricing-pack-label">One-time purchase</span>
                  </div>
                  <div className="pricing-credits-highlight" style={{ marginTop: 0 }}>
                    {pack.credits.toLocaleString()} credits
                  </div>
                  <ul className="pricing-pack-perks">
                    <li>Credits never expire</li>
                    <li>Use for all AI features</li>
                  </ul>
                  <Button
                    variant="secondary"
                    className="pricing-cta-btn"
                    onClick={() => handlePurchase(pack.id)}
                    disabled={loadingPlanId !== null}
                  >
                    {loadingPlanId === pack.id ? 'Redirecting…' : 'Buy Now'}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
